import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { BookHeart, Lock, Clock, ScrollText, Users, HeartHandshake, TreePine, Gem, ShieldCheck, Sprout, PenLine, Feather, ArrowRight, CheckCircle2 } from "lucide-react";

// Pagina pubblica riassuntiva: aggiornare qui man mano che il prodotto cresce.
const FEATURES = [
  { icon: BookHeart, title: "Custodisci i ricordi", text: "Testo, foto, video, voci e persino una dedica musicale. Ogni ricordo è un frammento di te, conservato con cura." },
  { icon: Clock, title: "Rilascio nel tempo", text: "Decidi quando ogni ricordo riaffiora: subito, in una data, a un evento («quando compirà 18 anni») o dopo la tua scomparsa." },
  { icon: ScrollText, title: "Testamento con firma digitale", text: "Scrivi le tue volontà, allega i documenti e firmali digitalmente con data. Un notaio può essere avvisato al momento giusto." },
  { icon: Users, title: "Tutori di fiducia", text: "Nomini almeno due persone care: solo insieme potranno comunicare la tua scomparsa, per la massima sicurezza." },
  { icon: HeartHandshake, title: "Eredi e portali dedicati", text: "Ogni erede ha una pagina personale, con un messaggio di benvenuto e i ricordi che hai scelto solo per lui." },
  { icon: TreePine, title: "Albero di Famiglia", text: "Risali le tue radici: genitori, nonni, bisnonni. Racconta la loro storia con foto e memorie, senza limiti di generazioni." },
  { icon: Sprout, title: "Continua la custodia", text: "Dopo di te, gli eredi possono scaricare l'archivio o importare i ricordi nella propria Eterna, tramandandoli ancora." },
  { icon: Gem, title: "Spazio su misura", text: "Inizi gratis. Quando vuoi, scegli un piano con più spazio e durata pluriennale, con un unico pagamento." },
];

const STEPS = [
  { n: "1", title: "Dichiarazione dei tutori", text: "Servono almeno due tutori a confermare la scomparsa. Solo insieme avviano il processo." },
  { n: "2", title: "Certificato di morte", text: "Un tutore, un erede o il notaio carica il certificato rilasciato dal Comune, firmato digitalmente dall'ente." },
  { n: "3", title: "Verifica ufficiale", text: "Gli uffici verificano il documento. Nessun contenuto viene rilasciato prima di questa verifica." },
  { n: "4", title: "Rilascio delle volontà", text: "Solo dopo l'approvazione, ricordi e testamento raggiungono i destinatari, come da tue istruzioni." },
];

const PROMISES = [
  "Totalmente privato: nulla è visibile senza il tuo permesso.",
  "Nessun rilascio senza una verifica ufficiale della scomparsa.",
  "Sei tu a decidere cosa, a chi e quando.",
  "Le memorie si tramandano di generazione in generazione.",
];

const ComeFunziona = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="cf-logo">
          <BookHeart className="w-7 h-7 text-primary" />
          <span className="font-serif text-2xl">Eterna</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/accedi" className="text-sm text-muted-foreground hover:text-[#2C2A29] transition-colors" data-testid="cf-login-link">Accedi</Link>
          <Button onClick={() => navigate("/accedi?mode=register")} className="rounded-full bg-[#2C2A29] hover:bg-black text-white" data-testid="cf-start-btn">Inizia ora</Button>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-10 pb-14 text-center fade-in">
        <div className="inline-flex items-center gap-2 text-sm text-primary bg-[#FBEDE7] px-4 py-1.5 rounded-full mb-6">
          <Feather className="w-4 h-4" /> Come funziona
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2C2A29]">
          Un lascito che parla<br /><span className="text-primary italic">al momento giusto</span>.
        </h1>
        <p className="mt-6 text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
          Eterna custodisce i tuoi ricordi e le tue volontà, e li consegna alle persone che ami secondo istruzioni precise —
          solo quando sarà il momento. Ecco come.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-border shadow-sm hover:-translate-y-1 hover:shadow-md transition-transform duration-200" data-testid={`cf-feature-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#FBEDE7] flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-serif text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-sm text-[#4e8f6f] bg-[#EAF3EE] px-4 py-1.5 rounded-full mb-4">
            <ShieldCheck className="w-4 h-4" /> Verifica ufficiale della scomparsa
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Massima sicurezza, in quattro passi</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Nessun ricordo viene mai svelato prima di una verifica ufficiale. Un processo pensato per proteggere te e i tuoi cari.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} className="relative bg-white rounded-2xl p-6 border border-border" data-testid={`cf-step-${i}`}>
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-serif text-lg mb-4">{s.n}</div>
              <h3 className="font-serif text-lg mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-3xl overflow-hidden bg-[#2C2A29] text-white grid lg:grid-cols-2">
          <div className="p-10 lg:p-14 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 text-sm text-white/70 mb-4"><PenLine className="w-4 h-4" /> La nostra promessa</div>
            <h2 className="font-serif text-3xl lg:text-4xl leading-tight">Ciò che conta davvero, custodito per sempre.</h2>
            <ul className="mt-6 space-y-3">
              {PROMISES.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-white/85" data-testid={`cf-promise-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#81B29A] shrink-0 mt-0.5" /> <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
            <Button onClick={() => navigate("/accedi?mode=register")} className="mt-8 rounded-full h-12 px-8 bg-primary hover:bg-[#c9694f] text-white self-start" data-testid="cf-cta-btn">
              Inizia gratis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="p-10 lg:p-14 flex items-center justify-center bg-[#26241f]">
            <div className="text-center">
              <TreePine className="w-16 h-16 text-[#81B29A] mx-auto mb-4" />
              <p className="font-serif text-2xl">La memoria della tua famiglia,<br />viva per chi verrà.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="cf-footer-logo"><BookHeart className="w-4 h-4 text-primary" /> Eterna</Link>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <a href="mailto:info@eterna.page" className="hover:text-[#2C2A29] transition-colors" data-testid="cf-footer-contact">info@eterna.page</a>
            <Link to="/privacy" className="hover:text-[#2C2A29] transition-colors" data-testid="cf-footer-privacy">Privacy</Link>
            <Link to="/termini" className="hover:text-[#2C2A29] transition-colors" data-testid="cf-footer-terms">Termini</Link>
            <span>Fondatore: Massimiliano Costantini</span>
            <span>© 2026 Eterna</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComeFunziona;
